import * as Tabs from '@radix-ui/react-tabs';
import { ampli, type PortfolioDataRefreshedProperties } from '@/ampli';
import type { ExpandedState, SortingState } from '@tanstack/react-table';
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import { usePrevious } from '@uidotdev/usehooks';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAddress } from 'viem';

import { HeaderConnectButton } from '@/components/Header/HeaderButtons';
import { FeedbackModal } from '@/components/Modal/FeedBackFormModal';

import { CHAIN_IDS, HYPERCORE_CHAIN_ID, NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { useHyperCoreAccountFeed } from '@/hooks/perps/useHyperCoreAccountFeed';
import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useTokensPnL } from '@/hooks/useTokensPnL';
import { useWalletChart } from '@/hooks/useWalletChart';
import { useWalletPnL } from '@/hooks/useWalletPnL';
import { useWalletPortfolio } from '@/hooks/useWalletPortfolio';
import { useWalletPositions } from '@/hooks/useWalletPositions';

import { tabCountStyles, tabsStyles } from '@/styles/tabs';
import { fontStyles } from '@/styles/typography';

import { classNames } from '@/utils/classnames';
import type { EthereumAddress, SolanaAddress } from '@/utils/models';
import type {
  GroupedTokenBalance,
  SortColumn,
  SortDirection,
  TokenBalance,
} from '@/utils/zerion/types';

import { portfolioColumns } from './columns';
import { HyperCorePositions } from './HyperCorePositions';
import { PortfolioHeader } from './PortfolioHeader';
import { PortfolioTable, TokenRowSkeleton } from './PortfolioTable';
import { PortfolioTriggerOrdersModal } from './PortfolioTriggerOrdersModal';
import type {
  ChainFilterOption,
  PortfolioRow,
  PortfolioTableMeta,
  TriggerOrderTarget,
} from './types';
import type { TokenUnrealizedPnL, WalletFilter, WalletFilterOption } from './types';
import { useActivityTradeMarkers } from './useActivityTradeMarkers';
import { usePortfolioFeedbackPrompt } from './usePortfolioFeedbackPrompt';
import {
  buildConnectedWalletOptions,
  buildPnLTokenBlocks,
  buildPortfolioRows,
  getWalletTotals,
  groupTokensByFungible,
  isGroupedToken,
  isSortColumn,
} from './utils';

type PortfolioTab = 'tokens' | 'positions';
const tabsClasses = tabsStyles({ variant: 'card' });
const tabClasses = tabsClasses.tab({ class: ['inline-flex', 'items-center', 'gap-1.5'] });

const pageShellClass = classNames(
  'bg-special-background-app',
  'bg-(image:var(--gradient-special-homepage))',
  'min-h-screen',
);

const containerClass = classNames(
  'bg-special-background-card',
  'rounded-[20px]',
  'desktop:max-w-[1240px]',
  'w-full',
  'min-w-0',
  // Match Homepage DiscoveryTable: horizontal scroll inside the card instead of clipping
  'overflow-x-auto',
  'no-scrollbar',
  'mx-auto',
);

interface PortfolioProps {
  /** Optional EVM address to view - if not provided, uses connected wallet */
  evmAddress?: string;
  /** Optional Solana address to view - if not provided, uses connected wallet */
  solanaAddress?: string;
  /**
   * Lifts wallet selection to a host that shares it with sibling tabs. When
   * set, the host also owns the selector, so the header hides its own.
   */
  walletSelection?: {
    selected: WalletFilter;
    onChange: (wallet: WalletFilter) => void;
  };
  /** Rendered inside a host page that already owns the background and header spacing. */
  embedded?: boolean;
}

export function Portfolio({
  evmAddress: propsEvmAddress,
  solanaAddress: propsSolanaAddress,
  walletSelection,
  embedded = false,
}: PortfolioProps = {}) {
  const { ethereumWallet, solanaWallet, isConnected, restoringWalletAddresses } =
    useMatchaWallets();
  const { formatFiat } = useFiatDisplay();
  const [selectedChain, setSelectedChain] = useState<number | 'all'>('all');
  const [ownSelectedWallet, setOwnSelectedWallet] = useState<WalletFilter>('all');
  const selectedWallet = walletSelection?.selected ?? ownSelectedWallet;
  const setSelectedWallet = walletSelection?.onChange ?? setOwnSelectedWallet;
  const [searchQuery, setSearchQuery] = useState('');
  const [showSmallBalances, setShowSmallBalances] = useState(false);
  const [showUnpricedTokens, setShowUnpricedTokens] = useState(false);
  const [triggerOrderTarget, setTriggerOrderTarget] = useState<TriggerOrderTarget | null>(null);
  const [chartPeriod, setChartPeriod] = useState<
    'hour' | 'day' | 'week' | 'month' | 'year' | 'max'
  >('week');

  const [sortColumn, setSortColumn] = useState<SortColumn>('value');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const [expanded, setExpanded] = useState<ExpandedState>({});
  const router = useRouter();
  const [selectedTable, setSelectedTable] = useState<PortfolioTab>('tokens');
  // `#positions` permalinks the tab. The hash never reaches the server, so it is read after
  // hydration and again on back/forward.
  useEffect(() => {
    setSelectedTable(router.asPath.endsWith('#positions') ? 'positions' : 'tokens');
  }, [router.asPath]);
  const selectTable = (value: string) => {
    setSelectedTable(value as PortfolioTab);
    router.replace(
      { pathname: router.pathname, query: router.query, hash: value === 'positions' ? value : '' },
      undefined,
      { shallow: true, scroll: false },
    );
  };

  // Public portfolios (e.g. /portfolio/<address>) must show only the address in
  // the URL — never the viewer's connected wallets — so don't fall back when
  // viewing someone else's portfolio.
  const isViewingOtherWallet = Boolean(propsEvmAddress || propsSolanaAddress);

  const evmAddress = isViewingOtherWallet
    ? propsEvmAddress
    : (ethereumWallet?.address?.toLowerCase() ?? restoringWalletAddresses?.ethereum);
  const solAddress = isViewingOtherWallet
    ? propsSolanaAddress
    : (solanaWallet?.address ?? restoringWalletAddresses?.solana);

  const enabled = Boolean(evmAddress || solAddress);

  const walletOptions = useMemo((): WalletFilterOption[] => {
    // Public portfolio page: show only the viewed address with a chain icon
    if (isViewingOtherWallet) {
      if (propsEvmAddress) {
        return [{ id: 'evm', label: 'EVM', address: propsEvmAddress, chainId: CHAIN_IDS.MAINNET }];
      }
      if (propsSolanaAddress) {
        return [
          { id: 'solana', label: 'Solana', address: propsSolanaAddress, chainId: CHAIN_IDS.SOLANA },
        ];
      }
      return [];
    }

    return buildConnectedWalletOptions(
      { address: evmAddress, wallet: ethereumWallet },
      { address: solAddress, wallet: solanaWallet },
    );
  }, [
    isViewingOtherWallet,
    propsEvmAddress,
    propsSolanaAddress,
    evmAddress,
    solAddress,
    ethereumWallet,
    solanaWallet,
  ]);

  // A host that owns the wallet selection also owns its fallback.
  useEffect(() => {
    if (walletSelection) return;
    if (isViewingOtherWallet && walletOptions.length === 1) {
      setSelectedWallet(walletOptions[0].id);
    } else if (selectedWallet !== 'all' && !walletOptions.some(({ id }) => id === selectedWallet)) {
      setSelectedWallet('all');
    }
  }, [walletSelection, isViewingOtherWallet, selectedWallet, walletOptions, setSelectedWallet]);

  const effectiveEvmAddress = selectedWallet === 'solana' ? undefined : evmAddress;
  const effectiveSolAddress = selectedWallet === 'evm' ? undefined : solAddress;

  const { allTokens, totalValueUsd, isLoading, isRefetching, error, refetch } = useWalletPositions({
    evmAddress: effectiveEvmAddress,
    solanaAddress: effectiveSolAddress,
    enabled,
  });
  const hyperCoreUser =
    effectiveEvmAddress && isAddress(effectiveEvmAddress) ? effectiveEvmAddress : undefined;
  const { positions: hyperCorePositions, accountStates } = useHyperCoreAccountFeed(hyperCoreUser);
  // The store is empty until the first clearinghouse snapshot (which always carries the main
  // dex), so an empty position list before then means "not loaded", not "no positions".
  const isPositionsLoading = !!hyperCoreUser && Object.keys(accountStates).length === 0;

  // Set when the user clicks refresh or retry; consumed once on the next fire
  // so the trigger can be stamped as `user_initiated`.
  const userInitiatedRefreshRef = useRef(false);
  const handleRefresh = useCallback(() => {
    userInitiatedRefreshRef.current = true;
    refetch();
  }, [refetch]);

  // Reflects the addresses actually fetched by `useWalletPositions` so the
  // event matches the refreshed dataset (e.g., 1 when the wallet filter is
  // narrowed to a single chain even though both wallets are connected).
  const walletCount = (effectiveEvmAddress ? 1 : 0) + (effectiveSolAddress ? 1 : 0);
  const chainCount = useMemo(
    () => new Set(allTokens.map((token) => token.chainId)).size,
    [allTokens],
  );

  // Fires `portfolio_data_refreshed` on the trailing edge of each completed
  // wallet positions fetch for the connected portfolio view.
  const lastFiredAtRef = useRef<number | null>(null);
  const hasFiredOnceRef = useRef(false);
  const isFetching = isLoading || isRefetching;
  const wasFetching = usePrevious(isFetching);

  useEffect(() => {
    // Bail until we observe a settled fetch (true -> false transition).
    if (!wasFetching || isFetching) {
      return;
    }

    // The fetch settled; consume the user-initiated flag now so it cannot
    // leak into a later auto-refresh if we skip the event below.
    const wasUserInitiated = userInitiatedRefreshRef.current;
    userInitiatedRefreshRef.current = false;

    if (isViewingOtherWallet || !isConnected || error != null || walletCount === 0) {
      return;
    }

    const now = Date.now();
    // `Math.max` guards against negative deltas from clock skew (NTP/battery).
    const refreshIntervalSeconds =
      lastFiredAtRef.current == null
        ? 0
        : Math.max(0, Math.round((now - lastFiredAtRef.current) / 1000));

    let refreshTrigger: PortfolioDataRefreshedProperties['refresh_trigger'];
    if (wasUserInitiated) {
      refreshTrigger = 'user_initiated';
    } else if (!hasFiredOnceRef.current) {
      const [navEntry] = performance.getEntriesByType(
        'navigation',
      ) as PerformanceNavigationTiming[];
      refreshTrigger = navEntry?.type === 'reload' ? 'browser_refresh' : 'page_load';
    } else {
      refreshTrigger = 'auto_refresh';
    }

    ampli.portfolioDataRefreshed({
      refresh_trigger: refreshTrigger,
      refresh_interval_seconds: refreshIntervalSeconds,
      portfolio_asset_count: allTokens.length,
      portfolio_total_value_usd: totalValueUsd,
      wallet_count: walletCount,
      chain_count: chainCount,
    });

    lastFiredAtRef.current = now;
    hasFiredOnceRef.current = true;
  }, [
    isFetching,
    wasFetching,
    isViewingOtherWallet,
    isConnected,
    error,
    walletCount,
    chainCount,
    allTokens.length,
    totalValueUsd,
  ]);

  const { pnl, isLoading: isPnLLoading } = useWalletPnL({
    evmAddress: effectiveEvmAddress,
    solanaAddress: effectiveSolAddress,
    enabled,
    chainId: selectedChain,
  });

  const { data: portfolio, isLoading: isPortfolioLoading } = useWalletPortfolio({
    evmAddress: effectiveEvmAddress,
    solanaAddress: effectiveSolAddress,
    enabled,
  });

  // Only feeds this component's own wallet selector; a host renders its own.
  const { data: unfilteredPortfolio } = useWalletPortfolio({
    evmAddress,
    solanaAddress: solAddress,
    enabled: enabled && !walletSelection,
  });

  const walletTotals = useMemo(() => getWalletTotals(unfilteredPortfolio), [unfilteredPortfolio]);

  const { chart: portfolioChart, isLoading: isChartLoading } = useWalletChart({
    evmAddress: effectiveEvmAddress,
    solanaAddress: effectiveSolAddress,
    period: chartPeriod,
    enabled,
    chainId: selectedChain,
  });

  const { markers: tradeMarkers } = useActivityTradeMarkers({
    funders: {
      ethereum: effectiveEvmAddress as EthereumAddress | undefined,
      solana: effectiveSolAddress as SolanaAddress | undefined,
    },
    chainIds: selectedChain === 'all' ? undefined : [String(selectedChain)],
    period: chartPeriod,
    chart: portfolioChart,
    enabled: enabled && !isViewingOtherWallet,
  });

  const pnlTokenBlocks = useMemo(() => buildPnLTokenBlocks(allTokens), [allTokens]);

  const { tokensPnL } = useTokensPnL({
    evmAddress: effectiveEvmAddress,
    solanaAddress: effectiveSolAddress,
    tokenBlocks: pnlTokenBlocks,
    enabled: enabled && pnlTokenBlocks.length > 0,
    fetchAll:
      sortColumn === 'unrealizedPnl' ||
      sortColumn === 'unrealizedReturn' ||
      showSmallBalances ||
      selectedChain !== 'all' ||
      searchQuery.trim().length > 0,
  });

  const getPnLForToken = useCallback(
    (token: TokenBalance): TokenUnrealizedPnL | undefined => {
      if (token.costBasisUsd !== undefined && token.balanceUsd != null) {
        return { unrealizedGain: token.balanceUsd - token.costBasisUsd };
      }
      const key = `${token.chainId}:${token.tokenAddress}`;
      return tokensPnL.get(key);
    },
    [tokensPnL],
  );

  const filteredTokens = useMemo(() => {
    let tokens = allTokens;

    if (selectedChain !== 'all') {
      tokens = tokens.filter((token) => token.chainId === selectedChain);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      tokens = tokens.filter((token) => {
        const nameMatch = token.name?.toLowerCase().includes(query);
        const symbolMatch = token.symbol?.toLowerCase().includes(query);
        const addressMatch = token.tokenAddress.toLowerCase() === query;
        return nameMatch || symbolMatch || addressMatch;
      });
    }

    return tokens;
  }, [allTokens, selectedChain, searchQuery]);

  const filteredTotalValue = useMemo(() => {
    if (selectedChain === 'all') return totalValueUsd;
    return filteredTokens.reduce((sum, token) => sum + (token.balanceUsd ?? 0), 0);
  }, [filteredTokens, selectedChain, totalValueUsd]);

  const { tokensWithPrice, tokensWithoutPrice } = useMemo(() => {
    const withPrice: TokenBalance[] = [];
    const withoutPrice: TokenBalance[] = [];

    for (const token of filteredTokens) {
      if (token.balanceUsd != null && token.balanceUsd >= 0) {
        withPrice.push(token);
      } else {
        withoutPrice.push(token);
      }
    }

    return { tokensWithPrice: withPrice, tokensWithoutPrice: withoutPrice };
  }, [filteredTokens]);

  const smallBalanceThreshold = useMemo(() => {
    if (filteredTotalValue <= 0) return 0;
    return filteredTotalValue * 0.01;
  }, [filteredTotalValue]);

  const groupedTokensWithPrice = useMemo(() => {
    if (selectedChain !== 'all') return null;
    return groupTokensByFungible(tokensWithPrice);
  }, [tokensWithPrice, selectedChain]);

  const MIN_VISIBLE_TOKENS = 10;

  const { largeBalanceTokens, smallBalanceTokens, smallBalancesTotal } = useMemo(() => {
    if (groupedTokensWithPrice) {
      const large: (TokenBalance | GroupedTokenBalance)[] = [];
      const small: (TokenBalance | GroupedTokenBalance)[] = [];
      let smallTotal = 0;

      for (let i = 0; i < groupedTokensWithPrice.length; i++) {
        const item = groupedTokensWithPrice[i];
        const value = isGroupedToken(item) ? item.totalBalanceUsd : (item.balanceUsd ?? 0);
        if (i < MIN_VISIBLE_TOKENS || value >= smallBalanceThreshold) {
          large.push(item);
        } else {
          small.push(item);
          smallTotal += value;
        }
      }

      return {
        largeBalanceTokens: large,
        smallBalanceTokens: small,
        smallBalancesTotal: smallTotal,
      };
    }

    const large: TokenBalance[] = [];
    const small: TokenBalance[] = [];
    let smallTotal = 0;

    for (let i = 0; i < tokensWithPrice.length; i++) {
      const token = tokensWithPrice[i];
      const value = token.balanceUsd ?? 0;
      if (i < MIN_VISIBLE_TOKENS || value >= smallBalanceThreshold) {
        large.push(token);
      } else {
        small.push(token);
        smallTotal += value;
      }
    }

    return { largeBalanceTokens: large, smallBalanceTokens: small, smallBalancesTotal: smallTotal };
  }, [tokensWithPrice, smallBalanceThreshold, groupedTokensWithPrice]);

  const getSortValue = useCallback(
    (item: TokenBalance | GroupedTokenBalance, column: SortColumn): number => {
      if (column === 'value') {
        return isGroupedToken(item) ? item.totalBalanceUsd : (item.balanceUsd ?? 0);
      }
      if (column === 'allocation') {
        const value = isGroupedToken(item) ? item.totalBalanceUsd : (item.balanceUsd ?? 0);
        return filteredTotalValue > 0 ? (value / filteredTotalValue) * 100 : 0;
      }
      if (column === 'unrealizedReturn') {
        if (isGroupedToken(item)) {
          let totalUnrealizedGain = 0;
          let totalCostBasis = 0;
          for (const position of item.positions) {
            const pnl = getPnLForToken(position);
            if (pnl) {
              totalUnrealizedGain += pnl.unrealizedGain;
              totalCostBasis += (position.balanceUsd ?? 0) - pnl.unrealizedGain;
            }
          }
          return totalCostBasis > 0 ? (totalUnrealizedGain / totalCostBasis) * 100 : 0;
        }
        const pnl = getPnLForToken(item);
        if (!pnl) return 0;
        const costBasis = (item.balanceUsd ?? 0) - pnl.unrealizedGain;
        return costBasis > 0 ? (pnl.unrealizedGain / costBasis) * 100 : 0;
      }
      if (column === 'unrealizedPnl') {
        if (isGroupedToken(item)) {
          let totalUnrealizedGain = 0;
          for (const position of item.positions) {
            const pnl = getPnLForToken(position);
            if (pnl) totalUnrealizedGain += pnl.unrealizedGain;
          }
          return totalUnrealizedGain;
        }
        const pnl = getPnLForToken(item);
        return pnl?.unrealizedGain ?? 0;
      }
      return 0;
    },
    [filteredTotalValue, getPnLForToken],
  );

  const sortTokens = useCallback(
    <T extends TokenBalance | GroupedTokenBalance>(tokens: T[]): T[] => {
      return [...tokens].sort((a, b) => {
        const aValue = getSortValue(a, sortColumn);
        const bValue = getSortValue(b, sortColumn);
        const diff = aValue - bValue;
        return sortDirection === 'desc' ? -diff : diff;
      });
    },
    [getSortValue, sortColumn, sortDirection],
  );

  const sortedTokensWithPrice = useMemo(() => {
    if (showSmallBalances) {
      return sortTokens([...largeBalanceTokens, ...smallBalanceTokens]);
    }
    return sortTokens(largeBalanceTokens);
  }, [largeBalanceTokens, smallBalanceTokens, showSmallBalances, sortTokens]);

  const portfolioRows = useMemo(() => {
    return buildPortfolioRows({
      tokens: sortedTokensWithPrice,
      getPnLForToken,
    });
  }, [sortedTokensWithPrice, getPnLForToken]);

  const unpricedRows = useMemo(() => {
    return buildPortfolioRows({
      tokens: tokensWithoutPrice,
      getPnLForToken,
    });
  }, [tokensWithoutPrice, getPnLForToken]);

  const sorting: SortingState = useMemo(
    () => [{ id: sortColumn, desc: sortDirection === 'desc' }],
    [sortColumn, sortDirection],
  );

  const handleSortingChange = useCallback(
    (updaterOrValue: SortingState | ((prev: SortingState) => SortingState)) => {
      const newSorting =
        typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
      if (newSorting.length > 0) {
        const { id, desc } = newSorting[0];
        if (isSortColumn(id)) setSortColumn(id);
        setSortDirection(desc ? 'desc' : 'asc');
      }
    },
    [sorting],
  );

  const portfolioMeta: PortfolioTableMeta = useMemo(
    () => ({
      totalPortfolioValue: filteredTotalValue,
      formatFiat,
      isWatchOnly: isViewingOtherWallet,
      disableActions: !!restoringWalletAddresses,
      onAddTriggerOrders: setTriggerOrderTarget,
    }),
    [filteredTotalValue, formatFiat, isViewingOtherWallet, restoringWalletAddresses],
  );

  const table = useReactTable<PortfolioRow>({
    data: portfolioRows,
    columns: portfolioColumns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualSorting: true,
    getSubRows: (row) => row.subRows,
    state: { sorting, expanded },
    onSortingChange: handleSortingChange,
    onExpandedChange: setExpanded,
    meta: { portfolioMeta },
  });

  // HyperCore positions follow the chain filter like the token rows do.
  const visiblePositions =
    selectedChain === 'all' || selectedChain === HYPERCORE_CHAIN_ID ? hyperCorePositions : [];

  const chainsWithTokens = useMemo<ChainFilterOption[]>(() => {
    const balancesByChain = new Map<number, number>();

    for (const token of allTokens) {
      const current = balancesByChain.get(token.chainId) ?? 0;
      const balanceUsd = token.balanceUsd != null && token.balanceUsd > 0 ? token.balanceUsd : 0;
      balancesByChain.set(token.chainId, current + balanceUsd);
    }

    const chains: ChainFilterOption[] = Array.from(balancesByChain, ([id, balanceUsd]) => ({
      id,
      name: NETWORK_NAME_PER_CHAIN_ID[id] ?? `Chain ${id}`,
      balanceUsd,
    })).sort((chainA, chainB) => chainB.balanceUsd - chainA.balanceUsd);

    return [{ id: 'all', name: 'All networks', balanceUsd: totalValueUsd }, ...chains];
  }, [allTokens, totalValueUsd]);

  useEffect(() => {
    if (
      !isLoading &&
      selectedChain !== 'all' &&
      !chainsWithTokens.some(({ id }) => id === selectedChain)
    ) {
      setSelectedChain('all');
    }
  }, [chainsWithTokens, isLoading, selectedChain]);

  const { feedbackModalOpen, setFeedbackModalOpen, handleFeedbackModalClose } =
    usePortfolioFeedbackPrompt({ enabled: !isViewingOtherWallet && isConnected });

  if (!enabled) {
    return (
      <div className={embedded ? undefined : pageShellClass}>
        {!embedded && <div className="h-[62px] [body[data-show-banner='true']_&]:h-[102px]" />}

        <div className="py-4 px-4">
          {!embedded && (
            <h1
              className={fontStyles({
                variant: 'H2',
                class: ['max-w-[1240px]', 'mx-auto', 'my-6', 'text-left'],
              })}
            >
              Portfolio
            </h1>
          )}

          <div
            className={classNames(
              containerClass,
              'flex items-center justify-center w-full h-[75vh]',
            )}
          >
            <div className="h-fit text-center">
              <span
                className={fontStyles({
                  variant: 'LABEL_LG',
                  class: ['flex justify-center mb-2'],
                })}
              >
                Your portfolio will appear here.
              </span>
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['flex justify-center mt-2 mb-4 text-token-gray-700'],
                })}
              >
                Connect wallet to see your token balances.
              </div>
              <span className="flex justify-center">
                <HeaderConnectButton />
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? undefined : pageShellClass}>
      {!embedded && <div className="h-[62px] [body[data-show-banner='true']_&]:h-[102px]" />}

      <div
        className={classNames(
          'pb-4 tablet:pb-0 mb-3 tablet:mb-13',
          embedded ? 'pt-0' : 'pt-4 tablet:pt-8',
        )}
      >
        <PortfolioHeader
          totalValue={filteredTotalValue}
          isLoading={isLoading}
          pnl={pnl}
          isPnLLoading={isPnLLoading}
          portfolio={portfolio}
          isPortfolioLoading={isPortfolioLoading}
          chart={portfolioChart}
          isChartLoading={isChartLoading}
          tradeMarkers={tradeMarkers}
          chartPeriod={chartPeriod}
          onChartPeriodChange={setChartPeriod}
          selectedChain={selectedChain}
          onChainChange={setSelectedChain}
          chainsWithTokens={chainsWithTokens}
          selectedWallet={selectedWallet}
          onWalletChange={setSelectedWallet}
          walletOptions={walletOptions}
          walletTotals={walletTotals}
          showWalletSelector={!walletSelection}
          isWatchOnly={isViewingOtherWallet}
          tokens={filteredTokens}
          isRefreshing={isRefetching}
          onRefresh={handleRefresh}
        />
        <div className="px-4">
          <Tabs.Root value={selectedTable} onValueChange={selectTable} className={containerClass}>
            <div className="px-7 py-4.5">
              <Tabs.List aria-label="portfolio tables" className={tabsClasses.container()}>
                <Tabs.Trigger className={tabClasses} value="tokens">
                  Tokens
                </Tabs.Trigger>
                <Tabs.Trigger className={tabClasses} value="positions">
                  Positions
                  {visiblePositions.length > 0 && (
                    <span className={tabCountStyles}>{visiblePositions.length}</span>
                  )}
                </Tabs.Trigger>
              </Tabs.List>
            </div>
            <Tabs.Content
              value="positions"
              className="flex flex-col min-h-[280px] tablet:min-h-[400px]"
            >
              {isPositionsLoading ? (
                Array.from({ length: 5 }).map((_, i) => <TokenRowSkeleton key={i} />)
              ) : visiblePositions.length > 0 ? (
                <HyperCorePositions positions={visiblePositions} />
              ) : (
                <div className="flex flex-1 items-center justify-center">
                  <span
                    className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-700' })}
                  >
                    No open Hyperliquid positions
                  </span>
                </div>
              )}
            </Tabs.Content>
            <Tabs.Content value="tokens" className="min-h-[280px] tablet:min-h-[400px]">
              <PortfolioTable
                table={table}
                isLoading={isLoading}
                error={error}
                onRetry={handleRefresh}
                hasTokens={allTokens.length > 0}
                hasFilteredTokens={filteredTokens.length > 0}
                selectedChain={selectedChain}
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                unpricedRows={unpricedRows}
                unpricedCount={tokensWithoutPrice.length}
                showUnpricedTokens={showUnpricedTokens}
                onShowUnpricedTokensChange={setShowUnpricedTokens}
                smallBalanceCount={smallBalanceTokens.length}
                smallBalancesTotal={smallBalancesTotal}
                showSmallBalances={showSmallBalances}
                onShowSmallBalancesChange={setShowSmallBalances}
              />
            </Tabs.Content>
          </Tabs.Root>
        </div>
      </div>
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onOpenChange={setFeedbackModalOpen}
        onClose={handleFeedbackModalClose}
        triggerSource="portfolio"
      />
      <PortfolioTriggerOrdersModal
        target={triggerOrderTarget}
        onClose={() => setTriggerOrderTarget(null)}
      />
    </div>
  );
}
